import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import {
    IAbortInputWorkflowProcessRequestBody,
    ICancelInputWorkflowRequestBody,
    IGetFileUploadUrlResponse
} from "src/network/NetworkTypes";
import { RootStore } from "src/stores/RootStore";
import { INVOICE_ORIGIN, INPUTWORKFLOW_STATE, INPUTWORKFLOW_FILE_STATE, INVOICE_FILE_TYPE } from "./InvoiceEnums";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { IInputWorkflow, IInputWorkflowFile, IInputWorkflowUploadFile, IRealestate } from "./InvoiceTypes";
import { INVALID_FILENAME_CHARS, replaceInvalidFileNameChars, replaceUmlauts } from "./Utils";
import {
    GET_INPUTWORKFLOWS,
    SUBSCRIBE_TO_INPUTWORKFLOWS,
    UPDATE_INPUTWORKFLOW_FILE_STATE,
    UPDATE_REALESTATE_ON_INPUTWORKFLOW_FILE
} from "src/api/cred";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { Subscription } from "zen-observable-ts";
import { GetInputWorkflows, GetInputWorkflows_inputworkflows } from "src/api/generated/GetInputWorkflows";
import {
    SubscribeToInputWorkflows,
    SubscribeToInputWorkflows_inputworkflows
} from "src/api/generated/SubscribeToInputWorkflows";
import {
    UpdateInputWorkflowFileState,
    UpdateInputWorkflowFileStateVariables
} from "src/api/generated/UpdateInputWorkflowFileState";
import { v4 as uuidv4 } from "uuid";
import i18next from "i18next";
import { MessageType } from "src/components/notifications/Notifier";
import Axios from "src/network/Axios";
import {
    UpdateRealestateIdOnInputworkflowFile,
    UpdateRealestateIdOnInputworkflowFileVariables
} from "src/api/generated/UpdateRealestateIdOnInputworkflowFile";

export enum LOADING_STATE {
    INIT,
    LOADING,
    UPDATING_INPUTWORKFLOW_FILE_STATE,
    STARTING_KREDI_FLOW,
    DELETING_INPUT_WORKFLOW,
    NONE
}

export const ACCEPTED_DATA_TYPES = ["application/pdf"];

export const ACCEPTED_EXTENSIONS = [".pdf"];

type RealestateDataMap = {
    [inputWorkflowFileId: string]: IRealestateField; // index 0: visa1, index 1: visa2, index 2: visa3
};

interface IRealestateField {
    currentSearchQuery: string;
    selectedQuery: string;
    selectedRealestateName: string;
}

export class ImportInvoiceFilesStore {
    rootStore: RootStore;

    areFilesBeingImported: boolean = false;
    isDraggingOverDropZone: boolean = false;
    fileInputRef: HTMLInputElement | null = null;

    currentlyProcessingInputWorkflows: IInputWorkflow[] = [];
    originalInputWorkflowStructure: GetInputWorkflows_inputworkflows[] = [];

    loadingState: LOADING_STATE = LOADING_STATE.INIT;

    inputWorkflowSubscription?: Subscription;

    isWorkflowFileStateUpdating: Set<string> = new Set();
    pendingRemovalWorkflowIds: Set<string> = new Set();
    displayedInputWorkflowDropdowns: Set<string> = new Set();

    isDeleteInputWorkflowModalDisplayed: boolean = false;
    inputWorkflowToDelete?: IInputWorkflow;

    realestateFields: RealestateDataMap = {}; // Every InputWorkflowFile with State = 10 can be assigned to a realestate. This datastructure is used to store the state of the searchqueries

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            areFilesBeingImported: observable,
            isDraggingOverDropZone: observable,
            fileInputRef: observable,
            currentlyProcessingInputWorkflows: observable,
            loadingState: observable,
            inputWorkflowSubscription: observable,
            isWorkflowFileStateUpdating: observable,
            displayedInputWorkflowDropdowns: observable,
            isDeleteInputWorkflowModalDisplayed: observable,
            inputWorkflowToDelete: observable,
            realestateFields: observable,

            setAreFilesBeingImported: action,
            setIsDraggingOverDropZone: action,
            setFileInputRef: action,
            loadInputWorkflows: action,
            setLoadingState: action,
            setCurrentlyProcessingInputWorkflows: action,
            startInputWorkflowSubscription: action,
            stopInputWorkflowSubscription: action,
            onInputWorkflowUpdateReceived: action,
            setInputWorkflowSubscription: action,
            init: action,
            initiateRealestateFields: action,
            dispose: action,
            removeInputWorkflowAfterTimeout: action,
            updateInputWorkWorkflowFileState: action,
            toggleInputWorkflowFileDropdowns: action,
            closeAllInputWorkflowFileDropdowns: action,
            openAllInputWorkflowFileDropdowns: action,
            uploadInputWorkflowFileToBlobStorage: action,
            deleteInputWorkflowFile: action,
            deleteInputWorkflow: action,
            abortInputWorkflow: action,
            setIsDeleteInputWorkflowModalDisplayed: action,
            setInputWorkflowToDelete: action,
            setCurrentRealestateSearchQuery: action,
            setSelectedRealestateQuery: action,
            setSelectedRealestateName: action,
            resetRealestate: action,
            resetRealestateFields: action,
            mapInputWorkflowFromDbToObject: action,

            workflowsReadyToCreateInvoices: computed,
            filteredInputWorkflows: computed
        });

        reaction(
            () => this.rootStore.invoiceListStore.invoiceFilter.query,
            (newQuery, oldQuery) => {
                // Check if the previous query was non-empty and the new one is empty.
                if (oldQuery && newQuery === "") {
                    this.closeAllInputWorkflowFileDropdowns();
                }
            }
        );
    }

    init = async () => {
        const realestateSearchStore = this.rootStore.realestateSearchStore;
        await realestateSearchStore.loadRealestateData();

        const loadedInputWorkflows = await this.loadInputWorkflows();

        this.initiateRealestateFields(loadedInputWorkflows);

        this.startInputWorkflowSubscription();
    };

    initiateRealestateFields = (loadedInputWorkflows: IInputWorkflow[]) => {
        loadedInputWorkflows.forEach((inputWorkflow) => {
            inputWorkflow.inputWorkflowFiles?.forEach((inputWorkflowFile) => {
                runInAction(() => {
                    if (inputWorkflowFile.id && inputWorkflowFile.state === INPUTWORKFLOW_FILE_STATE.READY) {
                        if (inputWorkflowFile.realestateId && inputWorkflowFile.realestate !== undefined) {
                            const formattedRealestateName = inputWorkflowFile.realestate
                                ? this.getFormattedRealestateFieldValue(inputWorkflowFile.realestate)
                                : "";

                            this.realestateFields[inputWorkflowFile.id] = {
                                currentSearchQuery: formattedRealestateName,
                                selectedQuery: formattedRealestateName,
                                selectedRealestateName: formattedRealestateName
                            };
                        } else {
                            this.realestateFields[inputWorkflowFile.id] = {
                                currentSearchQuery: "",
                                selectedQuery: "",
                                selectedRealestateName: ""
                            };
                        }
                    }
                });
            });
        });
    };

    dispose = async () => {
        this.stopInputWorkflowSubscription();
        this.pendingRemovalWorkflowIds.clear();
    };

    setIsDeleteInputWorkflowModalDisplayed = (isDeleteInputWorkflowModalDisplayed: boolean) => {
        this.isDeleteInputWorkflowModalDisplayed = isDeleteInputWorkflowModalDisplayed;
    };

    setInputWorkflowToDelete = (inputWorkflow: IInputWorkflow | undefined) => {
        this.inputWorkflowToDelete = inputWorkflow;
    };

    setAreFilesBeingImported = (areFilesBeingImported: boolean) => {
        this.areFilesBeingImported = areFilesBeingImported;
    };

    setIsDraggingOverDropZone = (isDraggingOverDropZone: boolean) => {
        this.isDraggingOverDropZone = isDraggingOverDropZone;
    };

    setFileInputRef = (fileInputRef: HTMLInputElement | null) => {
        this.fileInputRef = fileInputRef;
    };

    setCurrentlyProcessingInputWorkflows = (currentlyProcessingInputWorkflows: IInputWorkflow[]) => {
        this.currentlyProcessingInputWorkflows = currentlyProcessingInputWorkflows;
    };

    setLoadingState = (loadingState: LOADING_STATE) => {
        this.loadingState = loadingState;
    };

    setInputWorkflowSubscription = (inputWorkflowSubscription: Subscription | undefined) => {
        this.inputWorkflowSubscription = inputWorkflowSubscription;
    };

    setCurrentRealestateSearchQuery(inputWorkflowFileId: string, query: string) {
        this.realestateFields[inputWorkflowFileId].currentSearchQuery = query;
    }

    setSelectedRealestateQuery(inputWorkflowFileId: string, query: string) {
        this.realestateFields[inputWorkflowFileId].selectedQuery = query;
    }

    setSelectedRealestateName(inputWorkflowFileId: string, query: string) {
        this.realestateFields[inputWorkflowFileId].selectedRealestateName = query;
    }

    getCurrentRealestateSearchQuery = (inputWorkflowFileId: string): string => {
        return this.realestateFields[inputWorkflowFileId]?.currentSearchQuery ?? "";
    };

    getSelectedRealestateQuery = (inputWorkflowFileId: string): string => {
        return this.realestateFields[inputWorkflowFileId]?.selectedQuery ?? "";
    };

    getSelectedRealestateName = (inputWorkflowFileId: string): string => {
        return this.realestateFields[inputWorkflowFileId]?.selectedRealestateName ?? "";
    };

    getFormattedRealestateFieldValue = (realestate: IRealestate): string => {
        return `${realestate.name} [${realestate.number}]`;
    };

    isRealestateSelected = (inputWorkflowFileId: string): boolean => {
        let isRealestateSelected = false;

        this.currentlyProcessingInputWorkflows.forEach((inputWorkflow) => {
            inputWorkflow.inputWorkflowFiles?.forEach((inputWorkflowFile) => {
                if (inputWorkflowFile.id === inputWorkflowFileId) {
                    isRealestateSelected = inputWorkflowFile.realestateId !== undefined;
                }
            });
        });

        return isRealestateSelected;
    };

    resetRealestate = (inputWorkflowFileId: string) => {
        this.currentlyProcessingInputWorkflows.forEach((inputWorkflow) => {
            inputWorkflow.inputWorkflowFiles?.forEach((inputWorkflowFile) => {
                if (inputWorkflowFile.id === inputWorkflowFileId) {
                    runInAction(() => {
                        inputWorkflowFile.realestateId = undefined;
                        inputWorkflowFile.realestate = undefined;
                    });
                }
            });
        });

        this.resetRealestateFields(inputWorkflowFileId);
        this.updateRealestateOnInputWorkWorkflowFile(inputWorkflowFileId, null);
    };

    resetRealestateFields = (inputWorkflowFileId: string) => {
        this.realestateFields[inputWorkflowFileId].currentSearchQuery = "";
        this.realestateFields[inputWorkflowFileId].selectedQuery = "";
        this.realestateFields[inputWorkflowFileId].selectedRealestateName = "";
    };

    updateDataWithSelectedRealestate = (selectedRealestate: IRealestate | undefined, inputWorkflowFileId: string) => {
        // update realestateinfo in the datastructure
        if (selectedRealestate !== undefined) {
            this.currentlyProcessingInputWorkflows.forEach((inputWorkflow) => {
                inputWorkflow.inputWorkflowFiles?.forEach((inputWorkflowFile) => {
                    if (inputWorkflowFile.id === inputWorkflowFileId) {
                        runInAction(() => {
                            inputWorkflowFile.realestateId = selectedRealestate.id;
                            inputWorkflowFile.realestate = selectedRealestate;
                        });
                    }
                });
            });

            this.updateRealestateOnInputWorkWorkflowFile(inputWorkflowFileId, selectedRealestate.id);
        } else if (selectedRealestate === undefined) {
            this.updateRealestateOnInputWorkWorkflowFile(inputWorkflowFileId, null);
        }

        // Update the field-values with the selected RealestateVisaUser
        if (selectedRealestate) {
            const formattedRealestateName = this.getFormattedRealestateFieldValue(selectedRealestate);

            this.setCurrentRealestateSearchQuery(inputWorkflowFileId, formattedRealestateName);
            this.setSelectedRealestateQuery(inputWorkflowFileId, formattedRealestateName);
            this.setSelectedRealestateName(inputWorkflowFileId, formattedRealestateName);
        } else {
            this.resetRealestateFields(inputWorkflowFileId);
        }
    };

    toggleInputWorkflowFileDropdowns = (inputWorkflowId: string) => {
        if (this.displayedInputWorkflowDropdowns.has(inputWorkflowId)) {
            this.displayedInputWorkflowDropdowns.delete(inputWorkflowId);
        } else {
            this.displayedInputWorkflowDropdowns.add(inputWorkflowId);
        }
    };

    closeAllInputWorkflowFileDropdowns = () => {
        this.displayedInputWorkflowDropdowns.clear();
    };

    openAllInputWorkflowFileDropdowns = () => {
        this.currentlyProcessingInputWorkflows.forEach((inputWorkflow) => {
            this.displayedInputWorkflowDropdowns.add(inputWorkflow.id);
        });
    };

    mapInputWorkflowFromDbToObject = (inputWorkflow: GetInputWorkflows_inputworkflows): IInputWorkflow => {
        // Flatten files with level information
        const flattenFiles = (files: any[], level: number = 0): IInputWorkflowFile[] => {
            const sortedFiles = files.sort((a, b) => {
                const nameA = a.file?.name?.toLowerCase() || "";
                const nameB = b.file?.name?.toLowerCase() || "";
                return nameA.localeCompare(nameB);
            });

            return sortedFiles.flatMap((file) => {
                const realestate = file.realestate;

                let zip: string | null | undefined;
                let city: string | null | undefined;

                if (realestate && realestate.houses.length > 0) {
                    zip = realestate.houses[0].zip;
                    city = realestate.houses[0].city;
                }

                const currentFile: IInputWorkflowFile = {
                    id: file.id,
                    fileId: file.file?.id,
                    name: file.file?.name ?? undefined,
                    state: file.state,
                    parentFileId: file.parentfileid,
                    inputWorkflowId: file.inputworkflowid,
                    realestateId: file.realestateid,
                    realestate: {
                        id: realestate ? realestate.id : "",
                        number: realestate ? realestate.number : "",
                        name: realestate ? realestate.name : "",
                        zip: zip ?? "",
                        city: city ?? ""
                    },
                    level: level // Add level information
                };

                // Recursively process children and increase the level
                const childFiles = flattenFiles(file.children ?? [], level + 1);

                // Combine the current file with its children
                return [currentFile, ...childFiles];
            });
        };

        const inputWorkflowFiles: IInputWorkflowFile[] = flattenFiles(inputWorkflow.inputworkflowfiles);

        if (inputWorkflowFiles && inputWorkflowFiles.length > 0) {
            // if the inputWorkflowFileId doesn't exist in the realestateFields, create a new entry for each inputworkflowfile
            inputWorkflowFiles.forEach((inputWorkflowFile) => {
                if (inputWorkflowFile.id && !this.realestateFields[inputWorkflowFile.id]) {
                    this.realestateFields[inputWorkflowFile.id] = {
                        currentSearchQuery: "",
                        selectedQuery: "",
                        selectedRealestateName: ""
                    };
                }
            });
        }

        const inputWorkflowObject: IInputWorkflow = {
            id: inputWorkflow.id,
            emailId: inputWorkflow.emailid,
            origin: inputWorkflow.origin,
            state: inputWorkflow.state,
            workflowInstance: inputWorkflow.workflowinstance,
            inputWorkflowFiles: inputWorkflowFiles
        };

        return inputWorkflowObject;
    };

    loadInputWorkflows = async (): Promise<IInputWorkflow[]> => {
        this.setLoadingState(LOADING_STATE.LOADING);

        const { data }: { data: GetInputWorkflows } = await apolloClientInstance.query<GetInputWorkflows>({
            query: GET_INPUTWORKFLOWS,
            fetchPolicy: "no-cache"
        });

        const currentlyProcessingInputWorkflows: IInputWorkflow[] = data.inputworkflows.map((inputWorkflow) => {
            return this.mapInputWorkflowFromDbToObject(inputWorkflow);
        });

        this.setCurrentlyProcessingInputWorkflows(currentlyProcessingInputWorkflows);

        this.setLoadingState(LOADING_STATE.NONE);

        return currentlyProcessingInputWorkflows;
    };

    startInputWorkflowSubscription() {
        const inputWorkflowSubscription = apolloClientInstance
            .subscribe<SubscribeToInputWorkflows>({
                query: SUBSCRIBE_TO_INPUTWORKFLOWS
            })
            .subscribe({
                next: ({ data }) => {
                    if (data?.inputworkflows) {
                        this.onInputWorkflowUpdateReceived(data.inputworkflows);
                    }
                },
                error: () => {
                    // Error is logged by Apollo errorLink
                    this.stopInputWorkflowSubscription();
                }
            });

        this.setInputWorkflowSubscription(inputWorkflowSubscription);
    }

    stopInputWorkflowSubscription() {
        if (this.inputWorkflowSubscription) {
            this.inputWorkflowSubscription.unsubscribe();
            this.setInputWorkflowSubscription(undefined);
        }
    }

    onInputWorkflowUpdateReceived = (inputWorkflows: SubscribeToInputWorkflows_inputworkflows[]) => {
        const workflowsFromDb = inputWorkflows.map((wf) => this.mapInputWorkflowFromDbToObject(wf));

        const dbWorkflowIds = new Set(workflowsFromDb.map((wf) => wf.id));
        const uiOnlyWorkflows = this.currentlyProcessingInputWorkflows.filter(
            (wf) => wf.state === INPUTWORKFLOW_STATE.UI && !dbWorkflowIds.has(wf.id)
        );

        // Detect workflows that just disappeared from the subscription (transitioned to FINISHED)
        // and schedule them for removal after a timeout
        this.currentlyProcessingInputWorkflows.forEach((wf) => {
            if (
                wf.state !== INPUTWORKFLOW_STATE.UI &&
                !dbWorkflowIds.has(wf.id) &&
                !this.pendingRemovalWorkflowIds.has(wf.id)
            ) {
                this.pendingRemovalWorkflowIds.add(wf.id);
                this.removeInputWorkflowAfterTimeout(wf);
            }
        });

        // Keep workflows that are pending removal (showing FINISHED tag)
        const pendingRemovalWorkflows = this.currentlyProcessingInputWorkflows
            .filter((wf) => this.pendingRemovalWorkflowIds.has(wf.id) && !dbWorkflowIds.has(wf.id))
            .map((wf) => (wf.state !== INPUTWORKFLOW_STATE.FINISHED ? { ...wf, state: INPUTWORKFLOW_STATE.FINISHED } : wf));

        this.setCurrentlyProcessingInputWorkflows([...uiOnlyWorkflows, ...pendingRemovalWorkflows, ...workflowsFromDb]);
        this.isWorkflowFileStateUpdating.clear();
        this.initiateRealestateFields(workflowsFromDb);
    };

    removeInputWorkflowAfterTimeout = (inputWorkflowToRemove: IInputWorkflow) => {
        setTimeout(() => {
            this.pendingRemovalWorkflowIds.delete(inputWorkflowToRemove.id);
            const newStagingFilesArray = this.currentlyProcessingInputWorkflows.filter(
                (inputWorkflow) => inputWorkflow.id !== inputWorkflowToRemove.id
            );
            this.setCurrentlyProcessingInputWorkflows([...newStagingFilesArray]);
        }, 10000);
    };

    canKrediFlowBeStarted = (inputWorkflow: IInputWorkflow): boolean => {
        const canKrediFlowBeStarted = inputWorkflow.inputWorkflowFiles
            ? inputWorkflow.inputWorkflowFiles.every(
                  (inputWorkflowFile) =>
                      inputWorkflowFile.state && inputWorkflowFile.state >= INPUTWORKFLOW_FILE_STATE.READY
              )
            : false;

        return canKrediFlowBeStarted;
    };

    updateRealestateOnInputWorkWorkflowFile = async (
        inputworkflowFileId: string,
        realestateId: string | null
    ): Promise<boolean> => {
        let successfullyUpdated = false;

        try {
            const { data } = await apolloClientInstance.mutate<
                UpdateRealestateIdOnInputworkflowFile,
                UpdateRealestateIdOnInputworkflowFileVariables
            >({
                mutation: UPDATE_REALESTATE_ON_INPUTWORKFLOW_FILE,
                variables: {
                    inputWorkflowFileId: inputworkflowFileId,
                    realestateId: realestateId
                }
            });

            if (data && data.update_file_inputworkflowfiles && data.update_file_inputworkflowfiles.affected_rows > 0) {
                successfullyUpdated = true;
            }
        } catch (error) {
            console.error("Error updating realestate on inputworkflow file: ", error);
        }

        return successfullyUpdated;
    };

    updateInputWorkWorkflowFileState = async (
        inputworkflowFileId: string,
        newState: INPUTWORKFLOW_FILE_STATE
    ): Promise<boolean> => {
        this.isWorkflowFileStateUpdating.add(inputworkflowFileId);

        let successfullyUpdated = false;

        try {
            const { data } = await apolloClientInstance.mutate<
                UpdateInputWorkflowFileState,
                UpdateInputWorkflowFileStateVariables
            >({
                mutation: UPDATE_INPUTWORKFLOW_FILE_STATE,
                variables: {
                    inputworkflowfileid: inputworkflowFileId,
                    state: newState
                }
            });

            if (data && data.updated_inputworkflowfiles) {
                successfullyUpdated = true;
            }
        } catch (error) {
            console.error("Error updating inputworkflow file state: ", error);
        }

        return successfullyUpdated;
    };

    abortInputWorkflow = async (inputWorkflow: IInputWorkflow): Promise<boolean> => {
        let wasWorkflowSuccessfullyAborted = false;

        const abortInputWorkflowProcessRequestBody: IAbortInputWorkflowProcessRequestBody = {
            inputworkflowid: inputWorkflow.id
        };

        try {
            await Axios.getAuthInstance().post(
                NetworkConfig.abortInputWorkflowProcessUrl,
                abortInputWorkflowProcessRequestBody,
                {
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );
            wasWorkflowSuccessfullyAborted = true;
        } catch (error) {
            console.error("Error occured while trying create a new input workflow: ", error);
        }

        return wasWorkflowSuccessfullyAborted;
    };

    deleteInputWorkflow = async (inputWorkflow: IInputWorkflow): Promise<boolean> => {
        this.setLoadingState(LOADING_STATE.DELETING_INPUT_WORKFLOW);

        const inputWorkflowSuccessfullyAborted = await this.abortInputWorkflow(inputWorkflow);

        if (inputWorkflowSuccessfullyAborted) {
            const updatedInputWorkflows = this.currentlyProcessingInputWorkflows.filter(
                (workflow) => workflow.id !== inputWorkflow.id
            );

            this.setCurrentlyProcessingInputWorkflows(updatedInputWorkflows);
        }

        this.setIsDeleteInputWorkflowModalDisplayed(false);
        this.setLoadingState(LOADING_STATE.NONE);
        return inputWorkflowSuccessfullyAborted;
    };

    deleteInputWorkflowFile = async (inputWorkflowFileId: string): Promise<boolean> => {
        const stateSuccessfullyUpdated = await this.updateInputWorkWorkflowFileState(
            inputWorkflowFileId,
            INPUTWORKFLOW_FILE_STATE.DELETED
        );

        if (stateSuccessfullyUpdated) {
            this.currentlyProcessingInputWorkflows.forEach((inputWorkflow) => {
                const updatedInputWorkflowFiles = inputWorkflow.inputWorkflowFiles?.filter(
                    (inputWorkflowFile) => inputWorkflowFile.id !== inputWorkflowFileId
                );
                inputWorkflow.inputWorkflowFiles = updatedInputWorkflowFiles;
            });
        }

        return stateSuccessfullyUpdated;
    };

    uploadInputWorkflowFileToBlobStorage = async (
        inputWorkflowUploadFile: IInputWorkflowUploadFile
    ): Promise<{ inputWorkflowId: string; fileId: string } | undefined> => {
        let successfullyUploadedFile = undefined;

        // A file the browser reports with 0 bytes (e.g. an OneDrive "online only" placeholder) would be committed as an
        // empty blob and hang in "Wird analysiert" for hours (#26277) - reject it before anything is uploaded.
        if (!inputWorkflowUploadFile?.size) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow.document_import.error.empty_file", {
                    file_name: inputWorkflowUploadFile?.name ?? ""
                }),
                MessageType.ERROR
            );
            return undefined;
        }

        const newInputWorkflowId = uuidv4();
        const newFileId = uuidv4();

        try {
            const sasUrl = await this.getFileUploadUrl(
                newFileId,
                inputWorkflowUploadFile.extension ? inputWorkflowUploadFile.extension.toLowerCase() : "pdf"
            );

            if (!sasUrl) {
                console.error("Unable to get SAS URL for file upload.");
            }

            if (!inputWorkflowUploadFile) {
                console.error("No file provided for upload.");
            }

            const chunkSize = 4 * 1024 * 1024; // 4 MB
            const totalChunks = Math.ceil(inputWorkflowUploadFile.size / chunkSize);
            const uploadPromises = [];

            let cleanedUpFileName = replaceInvalidFileNameChars(inputWorkflowUploadFile.name ?? "");
            cleanedUpFileName = replaceUmlauts(cleanedUpFileName);

            // the file extension is always in lowercase (everything after the last dot)
            const lastDotIndex = cleanedUpFileName.lastIndexOf(".");
            if (lastDotIndex !== -1) {
                const extension = cleanedUpFileName.substring(lastDotIndex);
                let fileNameWithoutExtension = cleanedUpFileName.substring(0, lastDotIndex);

                if (fileNameWithoutExtension === "_") {
                    fileNameWithoutExtension = i18next.t("screens.kredi_flow.document");
                }

                cleanedUpFileName = fileNameWithoutExtension + extension.toLowerCase();
            }

            const tags = {
                name: cleanedUpFileName,
                type: INVOICE_FILE_TYPE.INPUTWORKFLOW_FILE.toString(),
                customerid: this.rootStore.authStore.user?.customerid ?? "",
                extension: inputWorkflowUploadFile.extension ? inputWorkflowUploadFile.extension.toLowerCase() : "",
                mimetype: inputWorkflowUploadFile.mimetype ?? "",
                filedate: inputWorkflowUploadFile.fileDate ?? "",
                inputworkflowid: newInputWorkflowId
            };

            const encodedTags = Object.entries(tags)
                .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
                .join("&");

            const uploadChunk = async (start: number, end: number, chunkIndex: number): Promise<void> => {
                const blockId = window.btoa(String(chunkIndex).padStart(5, "0"));
                const blobUrl = `${sasUrl}&comp=block&blockid=${blockId}`;
                const chunk = inputWorkflowUploadFile.fileObject.slice(start, end);

                try {
                    await Axios.getInstance().put(blobUrl, chunk, {
                        urlWithoutParams: sasUrl,
                        headers: {
                            "x-ms-blob-type": "BlockBlob"
                        }
                    });
                } catch (error: any) {
                    throw new Error(`Chunk upload failed with status: ${error.status}`);
                }
            };

            // Create all upload promises
            for (let i = 0; i < totalChunks; i++) {
                const start = i * chunkSize;
                const end = Math.min(start + chunkSize, inputWorkflowUploadFile.size);
                uploadPromises.push(uploadChunk(start, end, i));
            }

            // Wait for all chunks to finish uploading
            await Promise.all(uploadPromises);

            // Commit the block list
            const blockList = Array.from({ length: totalChunks }, (_, i) => window.btoa(String(i).padStart(5, "0")));

            const blockListXml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockList
                .map((id) => `<Uncommitted>${id}</Uncommitted>`)
                .join("")}</BlockList>`;

            const commitUrl = `${sasUrl}&comp=blocklist`;

            try {
                await Axios.getInstance().put(commitUrl, blockListXml, {
                    headers: {
                        "Content-Type": "application/xml",
                        "x-ms-tags": encodedTags
                    }
                });

                successfullyUploadedFile = {
                    inputWorkflowId: newInputWorkflowId,
                    fileId: newFileId
                };

                const tempInputWorkflow: IInputWorkflow = {
                    id: newInputWorkflowId,
                    emailId: "",
                    origin: INVOICE_ORIGIN.MANUAL,
                    state: INPUTWORKFLOW_STATE.UI,
                    workflowInstance: "",
                    uiName: cleanedUpFileName
                };

                this.setCurrentlyProcessingInputWorkflows([
                    tempInputWorkflow,
                    ...this.currentlyProcessingInputWorkflows
                ]);
            } catch (error: any) {
                console.error("Failed to commit block list: ", error);

                if (error.response?.status === 400) {
                    const responseData = error.response.data;
                    if (typeof responseData === "string" && responseData.includes("The tags specified are invalid")) {
                        const match = responseData.match(/<TagValue>(.*?)<\/TagValue>/);
                        const invalidTag = match ? match[1] : "";

                        const knownInvalidChars = invalidTag
                            .split("")
                            .filter((char) => INVALID_FILENAME_CHARS.includes(char));

                        // Also check for characters that are invalid in Azure tags but might not be in our known list
                        const azureInvalidChars = invalidTag.match(/[^a-zA-Z0-9 \+\-\.\/\:\=\_]/g) || [];

                        const uniqueInvalidChars = [...new Set([...knownInvalidChars, ...azureInvalidChars])].join(" ");

                        this.rootStore.uiStore.printStatusMessage(
                            `${i18next.t("screens.kredi_flow.document_import.error.invalid_characters")}${
                                uniqueInvalidChars ? `: ${uniqueInvalidChars}` : invalidTag ? `: ${invalidTag}` : "."
                            }`,
                            MessageType.ERROR
                        );
                    }
                }
            }
        } catch (error) {
            console.error("Error occurred during file upload: ", error);
        }

        return successfullyUploadedFile;
    };

    getFileUploadUrl = async (fileId: string, extension: string): Promise<any | undefined> => {
        let fileUploadUrl = undefined;

        try {
            const response = await Axios.getAuthInstance().get(
                `${NetworkConfig.getFileUploadUrl}?blobname=${fileId}.${extension}`,
                {
                    urlWithoutParams: NetworkConfig.getFileUploadUrl,
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );
            const fileUploadUrlResponse: IGetFileUploadUrlResponse = response.data;
            fileUploadUrl = fileUploadUrlResponse.data.sasuri;
        } catch (error) {
            console.error("Error occured while trying to get file upload url: ", error);
        }

        return fileUploadUrl;
    };

    startKrediFlow = async (inputWorkflow: IInputWorkflow): Promise<boolean> => {
        this.setLoadingState(LOADING_STATE.STARTING_KREDI_FLOW);

        // set the state of the krediflow to 7 (creating invoices)
        const updatedInputWorkflow = { ...inputWorkflow, state: INPUTWORKFLOW_STATE.CREATING_INVOICES };
        const updatedInputWorkflows = this.currentlyProcessingInputWorkflows.map((workflow) =>
            workflow.id === inputWorkflow.id ? updatedInputWorkflow : workflow
        );
        this.setCurrentlyProcessingInputWorkflows(updatedInputWorkflows);

        let wasKrediFlowSuccessfullyStarted = false;

        const cancelInputWorkflowVariables: ICancelInputWorkflowRequestBody = {
            inputworkflowid: inputWorkflow.id,
            cancelled: false
        };

        try {
            await Axios.getAuthInstance().post(NetworkConfig.cancelInputWorkflowUrl, cancelInputWorkflowVariables, {
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                }
            });
            wasKrediFlowSuccessfullyStarted = true;
        } catch (error) {
            console.error("Error occured while trying to start the kredi flow process: ", error);
        }

        if (wasKrediFlowSuccessfullyStarted) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow.document_import.kredi_flow_successfully_started"),
                MessageType.SUCCESS
            );
        } else {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow.document_import.kredi_flow_start_failed"),
                MessageType.ERROR
            );
        }

        this.setLoadingState(LOADING_STATE.NONE);
        return wasKrediFlowSuccessfullyStarted;
    };

    getInputWorkflowName = (inputWorkflow: IInputWorkflow): string => {
        return inputWorkflow.inputWorkflowFiles && inputWorkflow.inputWorkflowFiles.length > 0
            ? inputWorkflow.inputWorkflowFiles[0].name ?? ""
            : inputWorkflow.state === INPUTWORKFLOW_STATE.UI
            ? inputWorkflow.uiName ?? ""
            : i18next.t("screens.kredi_flow.document_import.list.new_input_workflow");
    };

    get workflowsReadyToCreateInvoices(): IInputWorkflow[] {
        return this.currentlyProcessingInputWorkflows.filter((workflow) => this.canKrediFlowBeStarted(workflow));
    }

    get filteredInputWorkflows(): IInputWorkflow[] {
        const currentQuery = this.rootStore.invoiceListStore.invoiceFilter.query;

        if (currentQuery === "") {
            return this.currentlyProcessingInputWorkflows;
        }

        return this.currentlyProcessingInputWorkflows.filter((inputWorkflow) => {
            const inputWorkflowName = this.getInputWorkflowName(inputWorkflow);

            const queryMatchesWorkflowName = this.doesQueryMatchWorkflowName(inputWorkflowName, currentQuery);
            const queryMatchesWorkflowFileName = this.doesQueryMatchWorkflowFileName(inputWorkflow, currentQuery);
            const queryMatchesWorkflowState = this.doesQueryMatchWorkflowState(inputWorkflow, currentQuery);
            const queryMatchesWorkflowFileState = this.doesQueryMatchWorkflowFileState(inputWorkflow, currentQuery);

            const resultOnlyFoundInWorkflowFileName = !queryMatchesWorkflowName && queryMatchesWorkflowFileName;

            this.handleDropdownToggle(inputWorkflow, resultOnlyFoundInWorkflowFileName, queryMatchesWorkflowFileState);

            return (
                queryMatchesWorkflowName ||
                queryMatchesWorkflowFileName ||
                queryMatchesWorkflowState ||
                queryMatchesWorkflowFileState
            );
        });
    }

    filteredRealestateData(inputWorkflowFileId: string): IRealestate[] {
        const realestateData = this.rootStore.realestateSearchStore.realestateData;

        let currentRealestateSearchQuery = this.getCurrentRealestateSearchQuery(inputWorkflowFileId);
        let selectedRealestateQuery = this.getSelectedRealestateQuery(inputWorkflowFileId);

        if (currentRealestateSearchQuery === selectedRealestateQuery) {
            return realestateData;
        }

        if (currentRealestateSearchQuery === "") {
            return realestateData;
        }

        const filteredRealestateData = realestateData.filter((realestate) => {
            const realestateName = `${realestate.nameZipCity} [${realestate.number}]`;

            return realestateName.toLowerCase().includes(currentRealestateSearchQuery.toLowerCase());
        });

        return filteredRealestateData;
    }

    private doesQueryMatchWorkflowName(inputWorkflowName: string | undefined, currentQuery: string): boolean {
        return inputWorkflowName?.toLowerCase().includes(currentQuery.toLowerCase()) ?? false;
    }

    private doesQueryMatchWorkflowFileName(inputWorkflow: IInputWorkflow, currentQuery: string): boolean {
        return (
            inputWorkflow.inputWorkflowFiles?.some((inputWorkflowFile) =>
                inputWorkflowFile.name?.toLowerCase().includes(currentQuery.toLowerCase())
            ) ?? false
        );
    }

    private doesQueryMatchWorkflowState(inputWorkflow: IInputWorkflow, currentQuery: string): boolean {
        return i18next
            .t(`screens.kredi_flow.document_import.input_workflow.state.${inputWorkflow.state}`)
            .toLowerCase()
            .includes(currentQuery.toLowerCase());
    }

    private doesQueryMatchWorkflowFileState(inputWorkflow: IInputWorkflow, currentQuery: string): boolean {
        return (
            inputWorkflow.inputWorkflowFiles?.some((inputWorkflowFile) =>
                i18next
                    .t(`screens.kredi_flow.document_import.input_workflow_file.state.${inputWorkflowFile.state}`)
                    .toLowerCase()
                    .includes(currentQuery.toLowerCase())
            ) ?? false
        );
    }

    private handleDropdownToggle(
        inputWorkflow: IInputWorkflow,
        resultOnlyFoundInWorkflowFileName: boolean,
        searchQueryContainedInWorkflowFileState: boolean
    ): void {
        if (resultOnlyFoundInWorkflowFileName || searchQueryContainedInWorkflowFileState) {
            if (!this.displayedInputWorkflowDropdowns.has(inputWorkflow.id)) {
                this.toggleInputWorkflowFileDropdowns(inputWorkflow.id);
            }
        }
    }
}
